/**
 * Offline upload outbox (camera roadmap Phase 5a, G4).
 *
 * A capture that fails to upload — tunnel, lift, dead wifi, backgrounded app —
 * is currently lost the moment the page goes away. This persists the blob and
 * its target so the upload can be retried later, including after a reload.
 *
 * Two properties matter more than the retry machinery:
 *
 *  1. **Blobs must not outlive their purpose.** A queued capture is user photo
 *     data sitting on the device. It is deleted on successful upload, on
 *     explicit discard, and on logout — the last one wired into
 *     `clearUserScopedState()`, which is the documented place for per-user
 *     caches.
 *  2. **A failed upload must never silently vanish.** Entries are only removed
 *     on success or on an explicit give-up, and `attempts`/`lastError` are
 *     persisted so a caller can surface "3 photos waiting" rather than
 *     pretending everything is fine.
 *
 * The storage backend is injected rather than reaching for `indexedDB`
 * directly, because jsdom has none and this repo has no IndexedDB shim
 * installed. The in-memory backend is what the tests drive; the IndexedDB one
 * is what the app uses.
 */

export interface OutboxEntry {
  /** Stable id, also the IndexedDB key. */
  id: string;
  /** The bytes to upload. */
  blob: Blob;
  /** Original filename, preserved for the multipart part. */
  filename: string;
  /** Where it goes: the endpoint plus whatever form fields the caller needs. */
  endpoint: string;
  fields: Record<string, string>;
  /** Bookkeeping. */
  createdAt: number;
  attempts: number;
  lastError?: string;
  /** Epoch ms before which this entry must not be retried (backoff). */
  nextAttemptAt: number;
}

export interface OutboxBackend {
  put(entry: OutboxEntry): Promise<void>;
  getAll(): Promise<OutboxEntry[]>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

/**
 * The IndexedDB database name.
 *
 * Exported so `resetClient.ts` can list it among the databases a clean-slate
 * reset deletes without re-typing the string — two copies of a database name
 * is how one of them ends up stale and a reset silently leaves user photo data
 * on the device.
 */
export const UPLOAD_OUTBOX_DB_NAME = 'emalino-upload-outbox';
const DB_NAME = UPLOAD_OUTBOX_DB_NAME;
const STORE = 'uploads';
const DB_VERSION = 1;

/** Max attempts before an entry is parked rather than retried forever. */
export const MAX_ATTEMPTS = 8;

/**
 * Exponential backoff with full jitter.
 *
 * Jitter is not decoration: without it every queued capture on every device
 * that lost the same wifi retries in lockstep the moment it returns, which is
 * how a flaky network becomes a thundering herd against the API.
 */
export function backoffDelayMs(
  attempts: number,
  random: () => number = Math.random,
): number {
  const base = Math.min(1000 * 2 ** Math.max(0, attempts - 1), 5 * 60_000);

  return Math.floor(base / 2 + random() * (base / 2));
}

/** In-memory backend — the test seam, and a safe fallback where IDB is absent. */
export function createMemoryOutboxBackend(): OutboxBackend {
  const rows = new Map<string, OutboxEntry>();

  return {
    async put(entry) {
      rows.set(entry.id, { ...entry });
    },
    async getAll() {
      return Array.from(rows.values()).map((r) => ({ ...r }));
    },
    async delete(id) {
      rows.delete(id);
    },
    async clear() {
      rows.clear();
    },
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** IndexedDB backend. Returns null where IndexedDB is unavailable. */
function createIndexedDbOutboxBackend(): OutboxBackend | null {
  if (typeof indexedDB === 'undefined') {
    return null;
  }

  return {
    async put(entry) {
      const db = await openDatabase();
      const tx = db.transaction(STORE, 'readwrite');
      await promisify(tx.objectStore(STORE).put(entry));
      db.close();
    },
    async getAll() {
      const db = await openDatabase();
      const tx = db.transaction(STORE, 'readonly');
      const rows = await promisify(tx.objectStore(STORE).getAll() as IDBRequest<OutboxEntry[]>);
      db.close();

      return rows;
    },
    async delete(id) {
      const db = await openDatabase();
      const tx = db.transaction(STORE, 'readwrite');
      await promisify(tx.objectStore(STORE).delete(id));
      db.close();
    },
    async clear() {
      const db = await openDatabase();
      const tx = db.transaction(STORE, 'readwrite');
      await promisify(tx.objectStore(STORE).clear());
      db.close();
    },
  };
}

export interface UploadOutboxOptions {
  backend?: OutboxBackend | null;
  /** Performs one upload attempt; resolves on success, rejects to retry. */
  send: (entry: OutboxEntry) => Promise<void>;
  now?: () => number;
  random?: () => number;
  isOnline?: () => boolean;
}

export interface FlushResult {
  uploaded: number;
  retried: number;
  parked: number;
  skipped: number;
}

export class UploadOutbox {
  private readonly backend: OutboxBackend;

  private readonly send: (entry: OutboxEntry) => Promise<void>;

  private readonly now: () => number;

  private readonly random: () => number;

  private readonly isOnline: () => boolean;

  private flushing = false;

  constructor(options: UploadOutboxOptions) {
    this.backend = options.backend ?? createIndexedDbOutboxBackend() ?? createMemoryOutboxBackend();
    this.send = options.send;
    this.now = options.now ?? (() => Date.now());
    this.random = options.random ?? Math.random;
    this.isOnline = options.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  }

  async enqueue(
    input: Omit<OutboxEntry, 'createdAt' | 'attempts' | 'nextAttemptAt'>,
  ): Promise<OutboxEntry> {
    const entry: OutboxEntry = {
      ...input,
      createdAt: this.now(),
      attempts: 0,
      nextAttemptAt: this.now(),
    };
    await this.backend.put(entry);

    return entry;
  }

  async pending(): Promise<OutboxEntry[]> {
    return this.backend.getAll();
  }

  async size(): Promise<number> {
    return (await this.backend.getAll()).length;
  }

  async discard(id: string): Promise<void> {
    await this.backend.delete(id);
  }

  /** Drop every queued blob. Called on logout — see clearUserScopedState(). */
  async clear(): Promise<void> {
    await this.backend.clear();
  }

  /**
   * Attempt every entry that is due.
   *
   * Re-entrancy is guarded because both `online` and `visibilitychange` can
   * fire within milliseconds of each other, and uploading the same blob twice
   * is exactly the duplicate the server-side dedup then has to clean up.
   */
  async flush(): Promise<FlushResult> {
    const result: FlushResult = { uploaded: 0, retried: 0, parked: 0, skipped: 0 };

    if (this.flushing || !this.isOnline()) {
      return result;
    }
    this.flushing = true;

    try {
      const entries = await this.backend.getAll();

      for (const entry of entries) {
        if (entry.nextAttemptAt > this.now()) {
          result.skipped += 1;
          continue;
        }

        try {
          await this.send(entry);
          await this.backend.delete(entry.id);
          result.uploaded += 1;
        } catch (err) {
          const attempts = entry.attempts + 1;
          const message = err instanceof Error ? err.message : String(err);

          if (attempts >= MAX_ATTEMPTS) {
            // Parked, NOT deleted: the blob is the user's photo and silently
            // dropping it is the one outcome this queue exists to prevent.
            await this.backend.put({
              ...entry,
              attempts,
              lastError: message,
              nextAttemptAt: Number.MAX_SAFE_INTEGER,
            });
            result.parked += 1;
            continue;
          }

          await this.backend.put({
            ...entry,
            attempts,
            lastError: message,
            nextAttemptAt: this.now() + backoffDelayMs(attempts, this.random),
          });
          result.retried += 1;
        }
      }
    } finally {
      this.flushing = false;
    }

    return result;
  }
}

/**
 * Drop every queued blob, whatever backend is in play.
 *
 * Exported as a free function so `clearUserScopedState()` can call it without
 * owning an outbox instance — the store teardown runs on the 401 path too,
 * where nothing has constructed one.
 */
export async function clearUploadOutbox(): Promise<void> {
  const backend = createIndexedDbOutboxBackend();
  if (backend === null) {
    return;
  }

  try {
    await backend.clear();
  } catch {
    // A device that cannot open IndexedDB has nothing queued to leak.
  }
}
